import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import LoadingSpinner from 'src/assets/images/LoadingSpinner';
import { RESOLUTIONS } from 'src/constants';

export interface DelayedLoaderTranslations {
  /** Title text to display */
  title: string;
  /** Message text to display */
  message: string;
  /** Dismiss button text */
  cta: string;
}

export interface DelayedLoaderProps {
  /** Delay in milliseconds before showing the loader (default: 500ms) */
  delay?: number;
  /** Translations object containing title, message, and cta */
  translations: DelayedLoaderTranslations;
  /** Whether to show the loader initially */
  show?: boolean;
  /** Custom onDismiss handler (optional) */
  onDismiss?: () => void;
}

const DelayedLoaderOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 999;
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const DelayedLoaderMessage = styled.div`
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 24px 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  text-align: center;
  min-width: 320px;
  animation: slideDown 0.3s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  .loading-icon {
    margin-bottom: 16px;

    svg {
      width: 50px;
      height: 50px;
      animation: spin 2s linear infinite;
    }

    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  }

  .message-title {
    font-weight: 600;
    color: #495057;
    margin-bottom: 8px;
    font-size: 16px;
  }

  .message-subtitle {
    color: #6c757d;
    font-size: 14px;
    line-height: 1.4;
  }

  .dismiss-button {
    margin-top: 16px;
    padding: 8px 16px;
    background: rgb(223, 252, 104);
    border: none;
    border-radius: 6px;
    color: #272e34;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(223, 252, 104, 0.8);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  @media (max-width: ${RESOLUTIONS.medium}px) {
    top: 60px;
    left: 16px;
    right: 16px;
    transform: translateY(0);
    min-width: auto;
    padding: 20px 24px;

    .loading-icon svg {
      width: 40px;
      height: 40px;
    }

    .message-title {
      font-size: 15px;
    }

    .message-subtitle {
      font-size: 13px;
    }

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }
`;

export const DelayedLoader: React.FC<DelayedLoaderProps> = ({
  delay = 500,
  translations,
  show = true,
  onDismiss,
}) => {
  const [showDelayedMessage, setShowDelayedMessage] = useState(false);

  useEffect(() => {
    if (!show) {
      setShowDelayedMessage(false);
      return;
    }

    const timer = setTimeout(() => {
      setShowDelayedMessage(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay, show]);

  const handleDismissLoader = () => {
    setShowDelayedMessage(false);
    onDismiss?.();
  };

  if (!showDelayedMessage) {
    return null;
  }

  return (
    <>
      <DelayedLoaderOverlay />
      <DelayedLoaderMessage>
        <div className='loading-icon'>
          <LoadingSpinner />
        </div>
        <div className='message-title'>{translations.title}</div>
        <div className='message-subtitle'>{translations.message}</div>
        <button className='dismiss-button' onClick={handleDismissLoader}>
          {translations.cta}
        </button>
      </DelayedLoaderMessage>
    </>
  );
};

export default DelayedLoader;
